import * as z from 'zod';

// Enums

// Where submissions go: `built_in` keeps them in tinct; `hubspot` stores them
// AND forwards each one as a HubSpot contact (requires a READY integration).
export const leadFormDestinationEnum = z.enum(['built_in', 'hubspot']);
export type LeadFormDestination = z.infer<typeof leadFormDestinationEnum>;

export const leadFormFieldTypeEnum = z.enum(['text', 'email', 'textarea', 'tel']);
export type LeadFormFieldType = z.infer<typeof leadFormFieldTypeEnum>;

// Per-submission forwarding lifecycle. `built_in` rows are terminal at
// `stored`; `hubspot` rows go stored → forwarded (with crmContactId) or
// forward_failed (forwardError set). No automatic retry in v1.
export const leadFormForwardStatusEnum = z.enum([
  'stored',
  'forwarded',
  'forward_failed',
]);
export type LeadFormForwardStatus = z.infer<typeof leadFormForwardStatusEnum>;

// Lead form field (embedded, ordered)

// Read shape — nested in the read schema, all nullable.
export const readLeadFormFieldSchema = z.object({
  key: z.string().nullable(),
  label: z.string().nullable(),
  type: leadFormFieldTypeEnum.nullable(),
  required: z.boolean().nullable(),
});
export type ReadLeadFormField = z.infer<typeof readLeadFormFieldSchema>;

// Write shape — the key is the HTML input name the generator renders
// verbatim; changing it after generation means the rendered form and the
// config disagree until a regeneration.
export const leadFormFieldSchema = z.object({
  key: z
    .string()
    .regex(
      /^[a-z0-9_]{1,40}$/,
      'Keys can only contain lowercase letters, digits and underscores (max 40)',
    ),
  label: z
    .string()
    .min(1, 'Label is required')
    .max(80, 'Label must not exceed 80 characters'),
  type: leadFormFieldTypeEnum,
  required: z.boolean(),
});
export type LeadFormField = z.infer<typeof leadFormFieldSchema>;

// 3. Read schema - all properties nullable (API response).
// GET always returns 200: an unconfigured page returns disabled defaults, so
// this one shape drives the whole settings screen.
export const readLeadFormSchema = z.object({
  enabled: z.boolean().nullable(),
  destination: leadFormDestinationEnum.nullable(),
  fields: z.array(readLeadFormFieldSchema).nullable(),
  successMessage: z.string().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadLeadForm = z.infer<typeof readLeadFormSchema>;

// 5. Update schema (PUT) - mirrors the backend validation.
export const updateLeadFormSchema = z.object({
  enabled: z.boolean(),
  destination: leadFormDestinationEnum,
  fields: z
    .array(leadFormFieldSchema)
    .min(1, 'At least one field is required')
    .max(12, 'At most 12 fields are allowed')
    .refine(
      (fields) => new Set(fields.map((field) => field.key)).size === fields.length,
      'Field keys must be unique',
    )
    .refine(
      (fields) => fields.filter((field) => field.type === 'email').length === 1,
      'Exactly one field must be of type email',
    ),
  successMessage: z
    .string()
    .min(1, 'Success message is required')
    .max(500, 'Success message must not exceed 500 characters'),
});
export type UpdateLeadForm = z.infer<typeof updateLeadFormSchema>;

// Submissions (read-only rows)

export const readLeadFormSubmissionSchema = z.object({
  id: z.uuid().nullable(),
  landingPageId: z.uuid().nullable(),
  email: z.string().nullable(),
  // The raw submitted values, keyed by the form's field keys.
  data: z.record(z.string(), z.string()).nullable(),
  destination: leadFormDestinationEnum.nullable(),
  forwardStatus: leadFormForwardStatusEnum.nullable(),
  crmContactId: z.string().nullable(),
  forwardError: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  createdAt: z.iso.datetime().nullable(),
});
export type ReadLeadFormSubmission = z.infer<typeof readLeadFormSubmissionSchema>;
